import { createFileRoute, Link } from "@tanstack/react-router";
import { FileText, Leaf, Shield, ArrowRight } from "lucide-react";
import { buildMeta } from "@/lib/seo";

export const Route = createFileRoute("/")({
  head: () =>
    buildMeta({
      title: "Harm Reduction, Cannabis-Reviews & Blog",
      description:
        "Gelebte Erfahrung trifft auf belastbare Daten. Philosophische Notizen, ehrliche Cannabis-Reviews, Safer-Use-Guides und ein Interaktions-Checker.",
      path: "/",
      image: "https://restlicht.fyi/og-home.jpg",
    }),
  component: Home,
});


type Section = "blog" | "medcan" | "hr";

const cards: Array<{
  to: "/blog" | "/medcan" | "/harm-reduction";
  title: string;
  desc: string;
  Icon: typeof FileText;
  section: Section;
}> = [
  {
    to: "/blog",
    title: "Blog",
    desc: "Persönliche, oft philosophische Beiträge: Gedanken, Projekte und Updates.",
    Icon: FileText,
    section: "blog",
  },
  {
    to: "/medcan",
    title: "MedCanReview",
    desc: "Ehrliche Reviews zu medizinischem Cannabis, mit Terpen- und Wirkprofilen.",
    Icon: Leaf,
    section: "medcan",
  },
  {
    to: "/harm-reduction",
    title: "Harm Reduction",
    desc: "Infos, Guides und Tools zur Schadensminimierung, inklusive Interaktions-Checker.",
    Icon: Shield,
    section: "hr",
  },
];

function Home() {
  return (
    <div data-section="home" style={{ paddingBottom: "3rem" }}>
      <section
        className="rl-ambient"
        style={{
          position: "relative",
          borderRadius: "var(--radius-lg)",
          border: "1px solid var(--accent-border)",
          background: "color-mix(in oklab, var(--bg-glass) 68%, transparent)",
          backdropFilter: "blur(18px) saturate(140%)",
          padding: "clamp(2.5rem, 7vw, 5rem) clamp(1rem, 4vw, 3rem)",
          marginBottom: "2.5rem",
          textAlign: "center",
          overflow: "hidden",
        }}
      >
        <span className="mono-badge" style={{ marginBottom: "1.5rem" }}>
          <span className="dot" />
          Restlicht.FYI
        </span>
        <h1
          className="font-display"
          style={{
            fontSize: "clamp(2.8rem, 10vw, 6.5rem)",
            fontWeight: 700,
            lineHeight: 0.95,
            letterSpacing: "-0.02em",
            margin: "0.75rem 0 0",
            color: "var(--text-primary)",
            textShadow: "0 0 32px color-mix(in oklab, var(--accent) 45%, transparent)",
          }}
        >
          <span aria-hidden="true">RESTLICHT</span>
          <span className="sr-only">Restlicht - Neons Nebenprojekte: Blog, Reviews und Harm Reduction</span>
        </h1>
        <div
          className="font-mono-rl"
          style={{
            marginTop: "1.5rem",
            display: "flex",
            flexWrap: "wrap",
            justifyContent: "center",
            alignItems: "center",
            gap: "0.5rem 1rem",
            fontSize: "0.85rem",
            letterSpacing: "0.2em",
            textTransform: "uppercase",
            color: "var(--text-secondary)",
          }}
        >
          <span style={{ color: "var(--accent-home)" }}>Harm Reduction</span>
          <span style={{ opacity: 0.4 }}>·</span>
          <span style={{ color: "var(--accent-medcan)" }}>Cannabis-Reviews</span>
          <span style={{ opacity: 0.4 }}>·</span>
          <span style={{ color: "var(--accent-blog)" }}>Blog</span>
        </div>
        <p
          style={{
            margin: "2rem auto 0",
            maxWidth: "42rem",
            color: "var(--text-secondary)",
            fontSize: "clamp(0.95rem, 1.6vw, 1.1rem)",
            lineHeight: 1.65,
          }}
        >
          Gelebte Erfahrung trifft auf belastbare Daten. Philosophische Notizen, ehrliche
          Cannabis-Reviews, Safer-Use-Guides und ein Interaktions-Checker, alles mit dem Ziel,
          Schaden zu minimieren.
        </p>
      </section>

      <div className="home-grid">
        {cards.map(({ to, title, desc, Icon, section }) => (
          <div key={to} data-section={section} style={{ display: "flex" }}>
            <Link
              to={to}
              className="glass-card"
              style={{
                position: "relative",
                display: "flex",
                flexDirection: "column",
                gap: "1rem",
                padding: "1.75rem",
                color: "var(--text-primary)",
                textDecoration: "none",
                width: "100%",
              }}
            >
              <div
                style={{
                  width: 44,
                  height: 44,
                  display: "grid",
                  placeItems: "center",
                  borderRadius: 12,
                  background: "var(--accent-bg)",
                  border: "1px solid var(--accent-border)",
                }}
              >
                <Icon size={20} color="var(--accent)" />
              </div>
              <div>
                <h2
                  className="font-display"
                  style={{ margin: 0, fontSize: "1.3rem", fontWeight: 600, letterSpacing: "-0.01em" }}
                >
                  {title}
                </h2>
                <p
                  style={{
                    margin: "0.5rem 0 0",
                    color: "var(--text-secondary)",
                    fontSize: "0.95rem",
                    lineHeight: 1.55,
                  }}
                >
                  {desc}
                </p>
              </div>
              <div
                className="font-mono-rl"
                style={{
                  marginTop: "auto",
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 8,
                  fontSize: "0.8rem",
                  textTransform: "uppercase",
                  letterSpacing: "0.18em",
                  color: "var(--accent-strong)",
                }}
              >
                Öffnen <ArrowRight size={14} />
              </div>
            </Link>
          </div>
        ))}
      </div>

      <footer
        className="font-mono-rl"
        style={{
          marginTop: "3rem",
          textAlign: "center",
          color: "var(--text-secondary)",
          fontSize: "0.8rem",
          textTransform: "uppercase",
          letterSpacing: "0.18em",
        }}
      >
        (c) {new Date().getFullYear()} Restlicht.FYI - Information, nicht Beratung.
      </footer>
    </div>
  );
}
